import React, { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { queuesApi, announcementsApi, clientsApi } from '../../../api/client';
import Modal, { Button, Badge } from '../../ui/Modal';
import { Label, Input, ErrorBanner } from '../../../auth/AuthShell';

/**
 * Expand a member-entry string like "60015-60030, 60049, 60050" into a sorted,
 * deduped array of valid 4–6 digit extensions.
 *
 * Returns { members: string[], errors: string[] } so the UI can surface bad
 * tokens (e.g. ranges that span > 100 entries, malformed entries).
 */
export function parseMemberSpec(spec) {
  const members = new Set();
  const errors = [];
  if (!spec || !spec.trim()) return { members: [], errors };
  const tokens = spec.split(/[\s,;]+/).filter(Boolean);
  for (const tok of tokens) {
    const range = tok.match(/^(\d{4,6})-(\d{4,6})$/);
    if (range) {
      const a = parseInt(range[1], 10);
      const b = parseInt(range[2], 10);
      if (b < a) {
        errors.push(`${tok}: end < start`);
        continue;
      }
      const len = b - a + 1;
      if (len > 200) {
        errors.push(`${tok}: range too large (${len} > 200)`);
        continue;
      }
      const width = range[1].length;
      for (let n = a; n <= b; n++) {
        members.add(String(n).padStart(width, '0'));
      }
      continue;
    }
    if (/^\d{4,6}$/.test(tok)) {
      members.add(tok);
      continue;
    }
    errors.push(`${tok}: not a 4–6 digit extension or range`);
  }
  return {
    members: Array.from(members).sort(),
    errors,
  };
}

const QUEUE_ID_RE = /^6\d{3,5}$/;

const STRATEGIES = [
  ['ringall',     'Ring all available members'],
  ['leastrecent', 'Least recently called'],
  ['fewestcalls', 'Fewest total calls'],
  ['random',      'Random'],
  ['rrmemory',    'Round robin (memory)'],
  ['linear',      'Linear (in order)'],
  ['wrandom',     'Weighted random'],
];

export default function QueueEditModal({ queue, agents = [], onClose, onSaved }) {
  const isNew = !queue;
  const [form, setForm] = useState(() => ({
    queue_id:           queue?.queue_id           || '',
    name:               queue?.name               || '',
    strategy:           queue?.strategy           || 'ringall',
    timeout:            queue?.timeout            || 20,
    wrapup_time:        queue?.wrapup_time        || 15,
    max_wait:           queue?.max_wait           || 300,
    pre_queue_ring_sec: queue?.pre_queue_ring_sec ?? 0,
    announce_holdtime:  queue?.announce_holdtime  || false,
    announce_position:  queue?.announce_position  || false,
    music_class:        queue?.music_class        || 'default',
    recording_enabled:  queue ? queue.recording_enabled : true,
    fallback_destination: queue?.fallback_destination || '',
    whisper_file:       queue?.whisper_file       || '',
    auto_answer:        queue ? !!queue.auto_answer : false,
    enabled:            queue ? queue.enabled : true,
    client_id:          queue?.client_id          || '',
  }));
  // Member spec is its own state so the user keeps their typed ranges. We
  // initialize from the existing flat list (e.g. when editing) but allow
  // free-form re-entry like "60015-60030, 60049".
  const [memberSpec, setMemberSpec] = useState(
    () => (queue?.members || []).join(', ')
  );
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Live-expanded view of whatever the admin typed.
  const { members: expandedMembers, errors: memberErrors } = useMemo(
    () => parseMemberSpec(memberSpec),
    [memberSpec]
  );

  // Quick-add helpers (replace OR append).
  const addMember = (ext) => {
    setMemberSpec((s) => {
      const trimmed = (s || '').trim();
      if (!trimmed) return ext;
      // Avoid trivial duplicates of the bare token.
      const tokens = trimmed.split(/[\s,;]+/).filter(Boolean);
      if (tokens.includes(ext)) return trimmed;
      return trimmed + ', ' + ext;
    });
  };

  const save = useMutation({
    mutationFn: async () => {
      if (isNew && !QUEUE_ID_RE.test(form.queue_id)) {
        throw new Error('Queue ID must start with 6 and be 4–6 digits (e.g. 60100).');
      }
      if (!form.name.trim()) throw new Error('Name is required.');
      if (memberErrors.length) {
        throw new Error(`Bad member entry: ${memberErrors[0]}`);
      }
      const payload = {
        ...(isNew ? { queue_id: form.queue_id } : {}),
        name: form.name.trim(),
        strategy: form.strategy,
        timeout: Number(form.timeout),
        wrapup_time: Number(form.wrapup_time),
        max_wait: Number(form.max_wait),
        pre_queue_ring_sec: Number(form.pre_queue_ring_sec) || 0,
        announce_holdtime: !!form.announce_holdtime,
        announce_position: !!form.announce_position,
        music_class: form.music_class || 'default',
        recording_enabled: !!form.recording_enabled,
        members: expandedMembers,
        fallback_destination: form.fallback_destination.trim() || null,
        whisper_file: form.whisper_file.trim() || null,
        auto_answer: !!form.auto_answer,
        enabled: !!form.enabled,
        client_id: form.client_id || null,
      };
      return isNew ? queuesApi.create(payload) : queuesApi.update(queue.id, payload);
    },
    onSuccess: onSaved,
    onError: (err) => setError(err?.detail || err?.message || 'Save failed.'),
  });

  const onSubmit = (e) => { e.preventDefault(); setError(null); save.mutate(); };

  // Whisper-category announcements (sourced from /api/admin/announcements/?category=whisper)
  const { data: clients = [] } = useQuery({
    queryKey: ['admin', 'clients'],
    queryFn: () => clientsApi.list(),
    staleTime: 60_000,
  });
  const { data: whisperAnnouncements = [] } = useQuery({
    queryKey: ['announcements-whisper'],
    queryFn:  () => announcementsApi.list('whisper'),
  });

  // Eligible members: any agent (or admin/supervisor with an extension)
  const eligibleMembers = agents
    .filter((a) => a.extension && a.enabled !== false)
    .sort((a, b) => (a.extension || '').localeCompare(b.extension || ''));

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={isNew ? 'New queue' : `Edit queue ${queue.queue_id}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={onSubmit} loading={save.isPending}>
            {isNew ? 'Create' : 'Save changes'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <ErrorBanner>{error}</ErrorBanner>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="queue_id">Queue ID</Label>
            <Input
              id="queue_id"
              value={form.queue_id}
              onChange={(e) => set('queue_id', e.target.value.replace(/\D/g, '').slice(0, 6))}
              disabled={!isNew}
              required
              pattern="6\d{3,5}"
              className="font-mono"
              placeholder="60100"
            />
            <p className="mt-1 text-xs text-surface-muted">Must start with 6 (4–6 digits). Reserved range for portal-managed queues.</p>
          </div>
          <div>
            <Label htmlFor="name">Display name</Label>
            <Input id="name" value={form.name} onChange={(e) => set('name', e.target.value)} required />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="strategy">Routing strategy</Label>
            <select
              id="strategy"
              value={form.strategy}
              onChange={(e) => set('strategy', e.target.value)}
              className="w-full px-3 py-2 rounded-thin bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border text-sm"
            >
              {STRATEGIES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="music_class">Music on hold</Label>
            <Input id="music_class" value={form.music_class} onChange={(e) => set('music_class', e.target.value)} placeholder="default" />
            <p className="mt-1 text-xs text-surface-muted">Asterisk MoH class name. <strong>default</strong> is always available.</p>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <div>
            <Label htmlFor="pre_queue_ring_sec">Pre-queue ringback (sec)</Label>
            <Input id="pre_queue_ring_sec" type="number" min={0} max={120} value={form.pre_queue_ring_sec} onChange={(e) => set('pre_queue_ring_sec', e.target.value)} />
            <p className="mt-1 text-xs text-surface-muted">Caller hears ringback for this long, then MoH while waiting. <strong>0</strong> = enter queue immediately.</p>
          </div>
          <div>
            <Label htmlFor="timeout">Member ring (sec)</Label>
            <Input id="timeout" type="number" min={5} max={600} value={form.timeout} onChange={(e) => set('timeout', e.target.value)} />
            <p className="mt-1 text-xs text-surface-muted">How long each agent's phone rings.</p>
          </div>
          <div>
            <Label htmlFor="wrapup_time">Wrap-up (sec)</Label>
            <Input id="wrapup_time" type="number" min={0} max={600} value={form.wrapup_time} onChange={(e) => set('wrapup_time', e.target.value)} />
          </div>
          <div>
            <Label htmlFor="max_wait">Max wait (sec, 0 = forever)</Label>
            <Input id="max_wait" type="number" min={0} max={86400} value={form.max_wait} onChange={(e) => set('max_wait', e.target.value)} />
          </div>
        </div>

        <div className="flex items-center gap-6 flex-wrap">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.recording_enabled} onChange={(e) => set('recording_enabled', e.target.checked)} />
            Record calls
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.auto_answer} onChange={(e) => set('auto_answer', e.target.checked)} />
            Auto-answer
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.announce_position} onChange={(e) => set('announce_position', e.target.checked)} />
            Announce queue position
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.announce_holdtime} onChange={(e) => set('announce_holdtime', e.target.checked)} />
            Announce hold time
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />
            Enabled
          </label>
        </div>

        <div>
          <Label htmlFor="members">Members</Label>
          <Input
            id="members"
            value={memberSpec}
            onChange={(e) => setMemberSpec(e.target.value)}
            placeholder="e.g. 60015-60030, 60049"
            className="font-mono"
          />
          <p className="mt-1 text-xs text-surface-muted">
            Comma-separated. Supports ranges (<code>60015-60030</code>) and individual extensions. Sorted &amp; deduped on save. Max 200 per range.
          </p>

          {/* Live preview of expanded members */}
          <div className="mt-2 p-2 rounded-thin bg-surface-subtle dark:bg-surface-dark-subtle">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-surface-muted">
                Expanded: <strong>{expandedMembers.length}</strong> extension{expandedMembers.length === 1 ? '' : 's'}
              </span>
              {memberErrors.length > 0 && (
                <span className="text-xs text-red-600 dark:text-red-300">
                  {memberErrors.length} error{memberErrors.length === 1 ? '' : 's'}
                </span>
              )}
            </div>
            {expandedMembers.length === 0 && memberErrors.length === 0 && (
              <span className="text-xs text-surface-muted">No members.</span>
            )}
            {expandedMembers.length > 0 && (
              <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                {expandedMembers.map((m) => (
                  <span key={m} className="font-mono text-xs px-1.5 py-0.5 rounded-full bg-continuum-50 dark:bg-continuum-900/40 text-continuum-700 dark:text-continuum-200">
                    {m}
                  </span>
                ))}
              </div>
            )}
            {memberErrors.length > 0 && (
              <ul className="mt-1 text-xs text-red-600 dark:text-red-300 list-disc list-inside">
                {memberErrors.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            )}
          </div>

          {/* Known-agents quick-add picker (additive — doesn't touch typed ranges) */}
          {eligibleMembers.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-continuum-600 dark:text-continuum-300 hover:underline">
                Browse provisioned agents ({eligibleMembers.length})
              </summary>
              <div className="mt-2 flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 border border-surface-border dark:border-surface-dark-border rounded-thin">
                {eligibleMembers.map((a) => {
                  const on = expandedMembers.includes(a.extension);
                  return (
                    <button
                      type="button"
                      key={a.extension}
                      onClick={() => addMember(a.extension)}
                      className={
                        'px-2 py-1 rounded-thin text-xs border transition-colors ' +
                        (on
                          ? 'bg-continuum-500 text-white border-continuum-500'
                          : 'border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle')
                      }
                      title={`${a.email} (${a.role}) — click to add to range`}
                    >
                      <span className="font-mono">{a.extension}</span> <span className="opacity-70">{a.name}</span>
                    </button>
                  );
                })}
              </div>
              <p className="mt-1 text-xs text-surface-muted">Click any to append to the range field.</p>
            </details>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="fallback_destination">Fallback destination</Label>
            <Input id="fallback_destination" value={form.fallback_destination} onChange={(e) => set('fallback_destination', e.target.value)} placeholder="e.g. 60001 or voicemail box" />
            <p className="mt-1 text-xs text-surface-muted">Where the call goes if max wait expires.</p>
          </div>
          <div>
            <Label htmlFor="whisper_file">Per-queue whisper announcement</Label>
            <select
              id="whisper_file"
              value={form.whisper_file}
              onChange={(e) => set('whisper_file', e.target.value)}
              className="w-full px-3 py-2 rounded-thin bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border text-sm"
            >
              <option value="">— None —</option>
              {whisperAnnouncements.map((a) => (
                <option key={a.id} value={`cx-announcements/${a.id}`}>
                  {a.name} ({a.voice})
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-surface-muted">
              Create in <strong>Admin › Announcements</strong> (category = Whisper).
            </p>
          </div>
          <div>
            <Label htmlFor="client_id">Assigned client (tenant)</Label>
            <select
              id="client_id"
              value={form.client_id}
              onChange={(e) => set('client_id', e.target.value)}
              className="w-full px-3 py-2 rounded-thin bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border text-sm"
            >
              <option value="">— Continuum-internal (no tenant) —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-surface-muted">
              Client users of this tenant will see this queue's ACD wallboards + recordings. Continuum employees always see everything.
            </p>
          </div>
        </div>

        <p className="text-xs text-surface-muted bg-surface-subtle dark:bg-surface-dark-subtle p-2 rounded-thin">
          Save here writes to the database. To push to <strong>LOU Issabel</strong> click <strong>↻ Re-sync all</strong> on the Queues page (or it'll happen next time anything else syncs).
        </p>
      </form>
    </Modal>
  );
}
